import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeftIcon } from '@heroicons/react/24/outline';
import AdminHeader from '../../../shared/components/AdminHeader';
import Modal from '../../../shared/components/Modal';
import Spinner from '../../../shared/components/Spinner';
import { getGradeLevelLabel } from '../../../shared/constant/gradeLevels';
import { confirmAction, showAlert, showSuccessToast } from '../../../shared/utils/dialogs';
import { getDistinctSectionGroups, getStudents } from '../../Student/services/studentService';
import {
  getAllClassSecretaries,
  removeSectionSecretary,
  setSectionSecretary,
} from '../services/secretaryAssignmentService';

const studentName = (s) => `${s.lastname}, ${s.firstname}`;
const sectionKey = (g) => `${g.grade_level}|${g.section}`;

// Lets an admin pick or clear the class secretary for one section: everyone in that section, a
// select defaulting to the current secretary (if any), and Save/Cancel.
function SecretaryPickerModal({ group, secretary, onClose, onSaved }) {
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedId, setSelectedId] = useState(secretary?.id ?? '');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let mounted = true;
    getStudents({ gradeLevel: group.grade_level, section: group.section, pageSize: 1000 })
      .then(({ data }) => {
        if (mounted) setStudents(data);
      })
      .catch((err) => showAlert({ text: err.message || 'Failed to load students' }))
      .finally(() => {
        if (mounted) setLoading(false);
      });
    return () => {
      mounted = false;
    };
  }, [group]);

  const handleSave = async () => {
    setSaving(true);
    try {
      if (!selectedId) {
        if (secretary) await removeSectionSecretary(secretary.id, studentName(secretary));
      } else {
        const student = students.find((s) => s.id === selectedId);
        await setSectionSecretary(selectedId, secretary?.id, studentName(student));
      }
      showSuccessToast('Class secretary updated');
      onSaved();
    } catch (err) {
      showAlert({ text: err.message || 'Failed to update class secretary' });
      setSaving(false);
    }
  };

  return (
    <Modal title={`${getGradeLevelLabel(group.grade_level)} - ${group.section}`} onClose={onClose}>
      {loading ? (
        <Spinner />
      ) : (
        <>
          <div className="space-y-1">
            <label htmlFor="secretary" className="text-sm font-medium text-gray-700">Class Secretary</label>
            <select
              id="secretary"
              value={selectedId}
              onChange={(e) => setSelectedId(e.target.value)}
              className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-[#4ea6d0]"
            >
              <option value="">None</option>
              {students.map((s) => (
                <option key={s.id} value={s.id}>{studentName(s)}</option>
              ))}
            </select>
          </div>

          <div className="flex justify-end gap-2 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="rounded-md border border-gray-300 px-4 py-2 text-sm font-medium text-gray-600 hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleSave}
              disabled={saving}
              className="rounded-md bg-[#0b4a71] px-4 py-2 text-sm font-medium text-white hover:bg-[#3f6d8e] disabled:opacity-50"
            >
              {saving ? 'Saving...' : 'Save'}
            </button>
          </div>
        </>
      )}
    </Modal>
  );
}

function SecretaryAssignmentPage() {
  const [groups, setGroups] = useState([]);
  const [secretaries, setSecretaries] = useState({});
  const [loading, setLoading] = useState(true);
  const [activeGroup, setActiveGroup] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [groupRows, secretaryRows] = await Promise.all([getDistinctSectionGroups(), getAllClassSecretaries()]);
      setGroups(groupRows);
      const map = {};
      secretaryRows.forEach((s) => {
        map[sectionKey(s)] = s;
      });
      setSecretaries(map);
    } catch (err) {
      showAlert({ text: err.message || 'Failed to load sections' });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const sectionLabel = (g) => `${getGradeLevelLabel(g.grade_level)} - ${g.section}`;

  const handleRemove = async (group, secretary) => {
    const confirmed = await confirmAction({
      title: 'Remove class secretary?',
      text: `${studentName(secretary)} will no longer be able to take attendance for ${sectionLabel(group)}.`,
      confirmText: 'Remove',
      danger: true,
    });
    if (!confirmed) return;

    try {
      await removeSectionSecretary(secretary.id, studentName(secretary));
      showSuccessToast('Class secretary removed');
      load();
    } catch (err) {
      showAlert({ text: err.message || 'Failed to remove class secretary' });
    }
  };

  return (
    <div className="h-dvh w-full flex flex-col bg-appbg overflow-hidden">
      <AdminHeader />

      <main className="flex-1 min-h-0 w-full p-4 lg:w-3/4 lg:mx-auto flex flex-col space-y-4">
        <div className="shrink-0 flex items-center gap-2">
          <Link to="/dashboard" aria-label="Back to dashboard" className="text-[#0b4a71] hover:text-[#3f6d8e]">
            <ArrowLeftIcon className="h-6 w-6" />
          </Link>
          <h1 className="text-2xl font-semibold text-[#0b4a71]">Secretary Assignment</h1>
        </div>

        <p className="shrink-0 text-sm text-gray-500">
          Assign one class secretary per section
        </p>

        <div className="flex-1 min-h-0 bg-white rounded-lg border border-gray-200 overflow-hidden flex flex-col">
          {loading ? (
            <Spinner />
          ) : groups.length === 0 ? (
            <p className="text-center text-gray-500 py-12">No sections found.</p>
          ) : (
            <div className="flex-1 min-h-0 overflow-x-auto overflow-y-auto">
              <table className="w-full text-sm">
                <thead className="bg-[#4ea6d0] text-left text-white sticky top-0 z-10">
                  <tr>
                    <th className="px-4 py-3 font-medium">Grade Level</th>
                    <th className="px-4 py-3 font-medium">Section</th>
                    <th className="px-4 py-3 font-medium">Class Secretary</th>
                    <th className="px-4 py-3 font-medium text-right">Action</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {groups.map((g) => {
                    const secretary = secretaries[sectionKey(g)];
                    return (
                      <tr key={sectionKey(g)} className="hover:bg-[#4ea6d0]/20">
                        <td className="px-4 py-3 text-gray-600">{getGradeLevelLabel(g.grade_level)}</td>
                        <td className="px-4 py-3 text-gray-600">{g.section}</td>
                        <td className="px-4 py-3 font-medium text-[#0b4a71]">
                          {secretary ? studentName(secretary) : <span className="font-normal text-gray-400">None</span>}
                        </td>
                        <td className="px-4 py-3 text-right">
                          <div className="flex justify-end gap-2">
                            <button
                              type="button"
                              onClick={() => setActiveGroup(g)}
                              className="rounded-md border border-[#4ea6d0] px-3 py-1.5 text-xs font-medium text-[#0b4a71] hover:bg-[#4ea6d0]/10"
                            >
                              {secretary ? 'Change' : 'Assign'}
                            </button>
                            {secretary && (
                              <button
                                type="button"
                                onClick={() => handleRemove(g, secretary)}
                                className="rounded-md border border-red-300 px-3 py-1.5 text-xs font-medium text-red-600 hover:bg-red-50"
                              >
                                Remove
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </main>

      {activeGroup && (
        <SecretaryPickerModal
          group={activeGroup}
          secretary={secretaries[sectionKey(activeGroup)]}
          onClose={() => setActiveGroup(null)}
          onSaved={() => {
            setActiveGroup(null);
            load();
          }}
        />
      )}
    </div>
  );
}

export default SecretaryAssignmentPage;
